import { hasOnlyValidEnglishNameCharacters } from '@cyren/common-lib';
import classNames from 'classnames';
import { isEmpty, trim } from 'lodash';
import { memo, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { V2QsTextProps } from './types';

/**
 * V2QsText 컴포넌트
 *
 * 텍스트 입력을 위한 컴포넌트
 * V1 QsTextInput의 V2 버전으로 Figma 디자인 시스템 적용
 *
 * @example
 * ```tsx
 * <V2QsText
 *   value={name}
 *   onChange={setName}
 *   placeholder="Enter your name"
 * />
 * ```
 */
function V2QsText({
  value = '',
  onChange,
  debounceMs = 300,
  placeholder = 'Enter text',
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  type = 'text',
  min,
  max,
  className,
  containerClassName,
  label,
  hint,
  autoFocus = false,
  restrictInput,
}: V2QsTextProps) {
  const [inputValue, setInputValue] = useState(value);
  const [debouncedValue] = useDebounce(inputValue, debounceMs);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialValueRef = useRef(value);

  // 외부 value 변경 시 동기화 (초기값과 다를 때만)
  useEffect(() => {
    if (initialValueRef.current !== value) {
      initialValueRef.current = value;
      setInputValue(value);
    }
  }, [value]);

  // 디바운스된 값이 변경되면 onChange 호출
  useEffect(() => {
    if (debouncedValue !== value) {
      onChange?.(debouncedValue);
    }
  }, [debouncedValue, onChange, value]);

  // 자동 포커스
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // 값 변경 핸들러 (내부 상태만 업데이트, onChange는 디바운스 후 호출)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  // min/max 유효성 검증 (number 타입)
  const isValidRange = () => {
    if (type !== 'number' || !inputValue) return true;
    const numValue = Number(inputValue);
    if (min !== undefined && numValue < min) return false;
    if (max !== undefined && numValue > max) return false;
    return true;
  };

  // restrictInput 유효성 검증
  const isValidRestrictInput = () => {
    if (!restrictInput || isEmpty(trim(inputValue))) return true;
    if (restrictInput === 'valid-name') {
      return hasOnlyValidEnglishNameCharacters(inputValue);
    }
    return true;
  };

  // restrictInput 에러 메시지
  const getRestrictInputErrorMessage = () => {
    if (restrictInput === 'valid-name' && !isValidRestrictInput()) {
      return 'Please enter a valid name (letters, spaces, hyphens, apostrophes only)';
    }
    return null;
  };

  const hasError = error || !isValidRange() || !isValidRestrictInput();
  const restrictInputError = getRestrictInputErrorMessage();

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Input wrapper 클래스
  const wrapperClasses = classNames(
    'flex items-center transition-all duration-200 rounded-[10px]',
    {
      // Width
      'w-full': fullWidth,
      'w-[260px]': !fullWidth,

      // Padding
      'px-5 py-[17px]': !inputValue,
      'px-5 py-3': inputValue,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,

      // Border
      'border-[1.5px]': true,
      'border-[#E6E9F5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'hover:border-[#DADDEB]': !disabled && !hasError,
      'focus-within:border-[#DADDEB]': !disabled && !hasError,
      'focus-within:bg-[#FAFBFF]': !disabled && !hasError,

      // Cursor
      'cursor-not-allowed': disabled,
    }
  );

  // Input 클래스
  const inputClasses = classNames(
    'w-full outline-none bg-transparent',
    'text-[15px] font-medium leading-[1.6]',
    {
      'text-[#1D1D1F]': !disabled,
      'text-[#C3C7D9]': disabled,
      'placeholder:text-[#AFB2C2]': true,
      'placeholder:uppercase': true,
      'cursor-not-allowed': disabled,
    },
    className
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // Range 힌트 텍스트
  const rangeHint =
    type === 'number' && (min !== undefined || max !== undefined)
      ? `${min ?? ''} ~ ${max ?? ''}`
      : null;

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Input Wrapper */}
      <div className={wrapperClasses}>
        <input
          ref={inputRef}
          type={type}
          className={inputClasses}
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          min={min}
          max={max}
        />
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Range Hint */}
      {rangeHint && !hasError && (
        <span className={hintClasses}>* {rangeHint}</span>
      )}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}

      {/* Range Error */}
      {!isValidRange() && !errorMessage && (
        <span className={errorClasses}>
          Value must be between {min ?? '-∞'} and {max ?? '∞'}
        </span>
      )}

      {/* RestrictInput Error */}
      {restrictInputError && !errorMessage && (
        <span className={errorClasses}>{restrictInputError}</span>
      )}
    </div>
  );
}

export default memo(V2QsText);
